import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';

import type { ConnectorItem, EntityItem } from '@toolproof-core/visualization/_contract/item';
import type { CollectionKey, ConstellationKey } from '@toolproof-core/visualization/_contract/key';

import type { RoleDirection } from '@/workflow/_contract/role';

export const WORKFLOW_KEYS = {
	constellations: {
		tool: `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Tool}` as ConstellationKey<'entity'>,
		resourceType: `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType}` as ConstellationKey<'entity'>,
	},
} as const;

const ROLE_COLLECTION_KEY_PREFIX = `collection:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:`;
const ROLE_CONSTELLATION_KEY_PREFIX = `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:`;
const TOOL_ROLE_CONNECTOR_COLLECTION_KEY_PREFIX = `collection:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_Tool}_${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:`;
const TOOL_ROLE_CONNECTOR_CONSTELLATION_KEY_PREFIX = `constellation:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_Tool}_${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:`;
const RESOURCE_TYPE_RADIAL_CONNECTOR_COLLECTION_KEY_PREFIX = `collection:connector:radial:${CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType}:`;
const RESOURCE_TYPE_RADIAL_CONNECTOR_CONSTELLATION_KEY_PREFIX = `constellation:connector:radial:${CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType}:`;
const WORKFLOW_STATE_ENTRY_CONNECTOR_COLLECTION_KEY_PREFIX = `collection:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}_${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:`;
const WORKFLOW_STATE_ENTRY_CONNECTOR_CONSTELLATION_KEY_PREFIX = `constellation:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}_${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:`;
const WORKFLOW_STATE_ENTRY_COLLECTION_KEY_PREFIX = `collection:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:`;
const WORKFLOW_STATE_ENTRY_CONSTELLATION_KEY_PREFIX = `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:`;
const CASE_COLLECTION_KEY_PREFIX = `collection:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Case}:`;
const CASE_CONSTELLATION_KEY_PREFIX = `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Case}:`;
const TOOL_STEP_COLLECTION_KEY_PREFIX = `collection:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_ToolStep}:`;
const TOOL_STEP_CONSTELLATION_KEY_PREFIX = `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_ToolStep}:`;
const TOOL_STEP_OUTER_RIM_KEY_PREFIX = 'tool-step-outer-rim:';
const MACRO_STEP_CONNECTOR_COLLECTION_KEY_PREFIX = `collection:connector:span:${CONSTANTS.Handles.ResourceTypes.TYPE_MacroStep}:`;
const MACRO_STEP_CONNECTOR_CONSTELLATION_KEY_PREFIX = `constellation:connector:span:${CONSTANTS.Handles.ResourceTypes.TYPE_MacroStep}:`;

export type WorkflowKeyScope = {
	workflowHandle: string;
	threadIndex: number;
};

export function normalizeRoleKeyPart(value: string): string {
	return encodeURIComponent(value);
}

export function buildToolItemKey(toolHandle: string): EntityItem['itemKey'] {
	return `item:entity:${normalizeRoleKeyPart(toolHandle)}` as EntityItem['itemKey'];
}

export function buildToolStepItemKey(stepKey: string): EntityItem['itemKey'] {
	return `item:entity:${normalizeRoleKeyPart(stepKey)}` as EntityItem['itemKey'];
}

export function buildToolStepCollectionKey(args: {
	workflowHandle: string;
	threadIndex: number;
}): CollectionKey<'entity'> {
	return `collection:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_ToolStep}:${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as CollectionKey<'entity'>;
}

export function parseToolStepCollectionKey(itemCollectionKey: string): {
	workflowHandle: string;
	threadIndex: number;
} | null {
	if (!itemCollectionKey.startsWith(TOOL_STEP_COLLECTION_KEY_PREFIX)) return null;

	const remainder = itemCollectionKey.slice(TOOL_STEP_COLLECTION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || extra !== undefined) return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
	};
}

export function buildToolStepConstellationKey(args: {
	workflowHandle: string;
	threadIndex: number;
}): ConstellationKey<'entity'> {
	return `constellation:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_ToolStep}:${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as ConstellationKey<'entity'>;
}

export function buildToolStepOuterRimItemKey(args: WorkflowKeyScope): EntityItem['itemKey'] {
	return `item:entity:${TOOL_STEP_OUTER_RIM_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as EntityItem['itemKey'];
}

export function buildToolStepOuterRimCollectionKey(args: WorkflowKeyScope): CollectionKey<'entity'> {
	return `collection:entity:${TOOL_STEP_OUTER_RIM_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as CollectionKey<'entity'>;
}

export function buildToolStepOuterRimConstellationKey(args: WorkflowKeyScope): ConstellationKey<'entity'> {
	return `constellation:entity:${TOOL_STEP_OUTER_RIM_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as ConstellationKey<'entity'>;
}

export function parseToolStepConstellationKey(constellationKey: string): {
	workflowHandle: string;
	threadIndex: number;
} | null {
	if (!constellationKey.startsWith(TOOL_STEP_CONSTELLATION_KEY_PREFIX)) return null;

	const remainder = constellationKey.slice(TOOL_STEP_CONSTELLATION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || extra !== undefined) return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
	};
}

export function buildRoleItemKey(args: {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
	roleName: string;
}): EntityItem['itemKey'] {
	const { toolHandle, direction, resourceTypeHandle, roleName } = args;
	return `item:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:${normalizeRoleKeyPart(toolHandle)}:${direction}:${normalizeRoleKeyPart(resourceTypeHandle)}:${normalizeRoleKeyPart(roleName)}` as EntityItem['itemKey'];
}

export function parseRoleItemKey(
	itemKey: string
): {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
	roleName: string;
} | null {
	const prefix = `item:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:`;
	if (!itemKey.startsWith(prefix)) return null;

	const remainder = itemKey.slice(prefix.length);
	const [encodedToolHandle, direction, encodedResourceTypeHandle, encodedRoleName] = remainder.split(':');
	if (!encodedToolHandle || !direction || !encodedResourceTypeHandle || !encodedRoleName) return null;
	if (direction !== 'input' && direction !== 'output') return null;

	return {
		toolHandle: decodeURIComponent(encodedToolHandle),
		direction,
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
		roleName: decodeURIComponent(encodedRoleName),
	};
}

export function buildRoleCollectionKey(args: {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
}): CollectionKey<'entity'> {
	const { toolHandle, direction, resourceTypeHandle } = args;
	return `${ROLE_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(toolHandle)}:${direction}:${normalizeRoleKeyPart(resourceTypeHandle)}` as CollectionKey<'entity'>;
}

export function buildRoleConstellationKey(args: {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
}): ConstellationKey<'entity'> {
	const { toolHandle, direction, resourceTypeHandle } = args;
	return `${ROLE_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(toolHandle)}:${direction}:${normalizeRoleKeyPart(resourceTypeHandle)}` as ConstellationKey<'entity'>;
}

export function parseRoleCollectionKey(
	collectionKey: string
): {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
} | null {
	if (!collectionKey.startsWith(ROLE_COLLECTION_KEY_PREFIX)) return null;

	const remainder = collectionKey.slice(ROLE_COLLECTION_KEY_PREFIX.length);
	const [encodedToolHandle, direction, encodedResourceTypeHandle] = remainder.split(':');
	if (!encodedToolHandle || !direction || !encodedResourceTypeHandle) return null;
	if (direction !== 'input' && direction !== 'output') return null;

	return {
		toolHandle: decodeURIComponent(encodedToolHandle),
		direction,
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
	};
}

export function parseRoleConstellationKey(
	constellationKey: string
): {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
} | null {
	if (!constellationKey.startsWith(ROLE_CONSTELLATION_KEY_PREFIX)) return null;

	const remainder = constellationKey.slice(ROLE_CONSTELLATION_KEY_PREFIX.length);
	const [encodedToolHandle, direction, encodedResourceTypeHandle] = remainder.split(':');
	if (!encodedToolHandle || !direction || !encodedResourceTypeHandle) return null;
	if (direction !== 'input' && direction !== 'output') return null;

	return {
		toolHandle: decodeURIComponent(encodedToolHandle),
		direction,
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
	};
}

export function buildToolRoleConnectorItemKey(args: {
	toolHandle: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
	roleName: string;
}): ConnectorItem['itemKey'] {
	const { toolHandle, direction, resourceTypeHandle, roleName } = args;
	return `item:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_Tool}_${CONSTANTS.Handles.ResourceTypes.TYPE_Role}:${normalizeRoleKeyPart(toolHandle)}:${direction}:${normalizeRoleKeyPart(resourceTypeHandle)}:${normalizeRoleKeyPart(roleName)}` as ConnectorItem['itemKey'];
}

export function buildToolRoleConnectorCollectionKey(args: {
	toolHandle: string;
}): CollectionKey<'connector'> {
	return `${TOOL_ROLE_CONNECTOR_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(args.toolHandle)}` as CollectionKey<'connector'>;
}

export function parseToolRoleConnectorCollectionKey(
	collectionKey: string
): {
	toolHandle: string;
} | null {
	if (!collectionKey.startsWith(TOOL_ROLE_CONNECTOR_COLLECTION_KEY_PREFIX)) return null;

	const encodedToolHandle = collectionKey.slice(TOOL_ROLE_CONNECTOR_COLLECTION_KEY_PREFIX.length);
	if (!encodedToolHandle) return null;

	return {
		toolHandle: decodeURIComponent(encodedToolHandle),
	};
}

export function buildToolRoleConnectorConstellationKey(args: {
	toolHandle: string;
}): ConstellationKey<'connector'> {
	return `${TOOL_ROLE_CONNECTOR_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(args.toolHandle)}` as ConstellationKey<'connector'>;
}

// ---------------------------------------------------------------------------
// ResourceType radial connector key codec
// ---------------------------------------------------------------------------

export function buildResourceTypeRadialConnectorItemKey(args: {
	resourceTypeItemKey: EntityItem['itemKey'];
	toolStepKey: string;
	direction: RoleDirection;
}): ConnectorItem['itemKey'] {
	return `item:connector:radial:${CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType}:${normalizeRoleKeyPart(args.resourceTypeItemKey)}:${normalizeRoleKeyPart(args.toolStepKey)}:${args.direction}` as ConnectorItem['itemKey'];
}

export function parseResourceTypeRadialConnectorItemKey(itemKey: string): {
	resourceTypeItemKey: EntityItem['itemKey'];
	toolStepKey: string;
	direction: RoleDirection;
} | null {
	const prefix = `item:connector:radial:${CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType}:`;
	if (!itemKey.startsWith(prefix)) return null;
	const [encodedResourceTypeItemKey, encodedToolStepKey, direction, extra] =
		itemKey.slice(prefix.length).split(':');
	if (!encodedResourceTypeItemKey || !encodedToolStepKey || extra !== undefined) return null;
	if (direction !== 'input' && direction !== 'output') return null;
	return {
		resourceTypeItemKey: decodeURIComponent(encodedResourceTypeItemKey) as EntityItem['itemKey'],
		toolStepKey: decodeURIComponent(encodedToolStepKey),
		direction,
	};
}

export function buildResourceTypeRadialConnectorCollectionKey(args: WorkflowKeyScope): CollectionKey<'connector'> {
	return `${RESOURCE_TYPE_RADIAL_CONNECTOR_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as CollectionKey<'connector'>;
}

export function parseResourceTypeRadialConnectorCollectionKey(
	collectionKey: string
): WorkflowKeyScope | null {
	if (!collectionKey.startsWith(RESOURCE_TYPE_RADIAL_CONNECTOR_COLLECTION_KEY_PREFIX)) return null;

	const remainder = collectionKey.slice(RESOURCE_TYPE_RADIAL_CONNECTOR_COLLECTION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || extra !== undefined) return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
	};
}

export function buildResourceTypeRadialConnectorConstellationKey(args: WorkflowKeyScope): ConstellationKey<'connector'> {
	return `${RESOURCE_TYPE_RADIAL_CONNECTOR_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as ConstellationKey<'connector'>;
}

// ---------------------------------------------------------------------------
// Macro-step connector key codec
// ---------------------------------------------------------------------------

export function buildMacroStepConnectorItemKey(args: {
	logicalMacroStepKey: string;
}): ConnectorItem['itemKey'] {
	return `item:connector:span:${CONSTANTS.Handles.ResourceTypes.TYPE_MacroStep}:${normalizeRoleKeyPart(args.logicalMacroStepKey)}` as ConnectorItem['itemKey'];
}

export function buildMacroStepConnectorCollectionKey(args: WorkflowKeyScope): CollectionKey<'connector'> {
	return `${MACRO_STEP_CONNECTOR_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as CollectionKey<'connector'>;
}

export function parseMacroStepConnectorCollectionKey(
	collectionKey: string
): {
	workflowHandle: string;
	threadIndex: number;
} | null {
	if (!collectionKey.startsWith(MACRO_STEP_CONNECTOR_COLLECTION_KEY_PREFIX)) return null;

	const remainder = collectionKey.slice(MACRO_STEP_CONNECTOR_COLLECTION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || extra !== undefined) return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
	};
}

export function buildMacroStepConnectorConstellationKey(args: WorkflowKeyScope): ConstellationKey<'connector'> {
	return `${MACRO_STEP_CONNECTOR_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}` as ConstellationKey<'connector'>;
}

// ---------------------------------------------------------------------------
// Workflow-state link connector key codec
// ---------------------------------------------------------------------------

export function buildWorkflowStateEntryConnectorItemKey(args: {
	targetStepKey: string;
	targetRoleName: string;
	sourceStepKey: string;
	sourceRoleName: string;
	sourceDirection: RoleDirection;
	resourceTypeHandle: string;
}): ConnectorItem['itemKey'] {
	const {
		targetStepKey,
		targetRoleName,
		sourceStepKey,
		sourceRoleName,
		sourceDirection,
		resourceTypeHandle,
	} = args;

	return `item:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}_${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:${normalizeRoleKeyPart(resourceTypeHandle)}:${sourceDirection}:${normalizeRoleKeyPart(targetStepKey)}:${normalizeRoleKeyPart(targetRoleName)}:${normalizeRoleKeyPart(sourceStepKey)}:${normalizeRoleKeyPart(sourceRoleName)}` as ConnectorItem['itemKey'];
}

export function buildWorkflowStateEntryConnectorCollectionKey(args: {
	resourceTypeHandle: string;
} & WorkflowKeyScope): CollectionKey<'connector'> {
	return `${WORKFLOW_STATE_ENTRY_CONNECTOR_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}:${normalizeRoleKeyPart(args.resourceTypeHandle)}` as CollectionKey<'connector'>;
}

export function parseWorkflowStateEntryConnectorItemKey(itemKey: string): {
	targetStepKey: string;
	sourceStepKey: string;
} | null {
	const prefix = `item:connector:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}_${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:`;
	if (!itemKey.startsWith(prefix)) return null;
	const parts = itemKey.slice(prefix.length).split(':');
	if (parts.length !== 6 || parts.some((part) => part.length === 0)
		|| (parts[1] !== 'input' && parts[1] !== 'output')) return null;
	return { targetStepKey: decodeURIComponent(parts[2]!), sourceStepKey: decodeURIComponent(parts[4]!) };
}

export function parseWorkflowStateEntryConnectorCollectionKey(
	collectionKey: string
): {
	workflowHandle: string;
	threadIndex: number;
	resourceTypeHandle: string;
} | null {
	if (!collectionKey.startsWith(WORKFLOW_STATE_ENTRY_CONNECTOR_COLLECTION_KEY_PREFIX)) return null;

	const remainder = collectionKey.slice(WORKFLOW_STATE_ENTRY_CONNECTOR_COLLECTION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, encodedResourceTypeHandle, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || !encodedResourceTypeHandle || extra !== undefined) return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
	};
}

export function buildWorkflowStateEntryConnectorConstellationKey(args: {
	resourceTypeHandle: string;
} & WorkflowKeyScope): ConstellationKey<'connector'> {
	return `${WORKFLOW_STATE_ENTRY_CONNECTOR_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}:${normalizeRoleKeyPart(args.resourceTypeHandle)}` as ConstellationKey<'connector'>;
}

// ---------------------------------------------------------------------------
// Workflow-state entry key codec
// ---------------------------------------------------------------------------

export function buildWorkflowStateEntryItemKey(args: {
	stepKey: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
	roleName: string;
}): EntityItem['itemKey'] {
	const { stepKey, direction, resourceTypeHandle, roleName } = args;
	return `item:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:${normalizeRoleKeyPart(stepKey)}:${direction}:${normalizeRoleKeyPart(resourceTypeHandle)}:${normalizeRoleKeyPart(roleName)}` as EntityItem['itemKey'];
}

export function parseWorkflowStateEntryItemKey(
	itemKey: string
): {
	stepKey: string;
	direction: RoleDirection;
	resourceTypeHandle: string;
	roleName: string;
} | null {
	const prefix = `item:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowStateEntry}:`;
	if (!itemKey.startsWith(prefix)) return null;

	const remainder = itemKey.slice(prefix.length);
	const [encodedStepKey, direction, encodedResourceTypeHandle, encodedRoleName] = remainder.split(':');
	if (!encodedStepKey || !direction || !encodedResourceTypeHandle || !encodedRoleName) return null;
	if (direction !== 'input' && direction !== 'output') return null;

	return {
		stepKey: decodeURIComponent(encodedStepKey),
		direction,
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
		roleName: decodeURIComponent(encodedRoleName),
	};
}

export function buildWorkflowStateEntryCollectionKey(args: {
	stepKey: string;
	resourceTypeHandle: string;
	direction: RoleDirection;
} & WorkflowKeyScope): CollectionKey<'entity'> {
	return `${WORKFLOW_STATE_ENTRY_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}:${normalizeRoleKeyPart(args.stepKey)}:${normalizeRoleKeyPart(args.resourceTypeHandle)}:${args.direction}` as CollectionKey<'entity'>;
}

export function parseWorkflowStateEntryCollectionKey(
	collectionKey: string
): {
	workflowHandle: string;
	threadIndex: number;
	stepKey: string;
	resourceTypeHandle: string;
	direction: RoleDirection;
} | null {
	if (!collectionKey.startsWith(WORKFLOW_STATE_ENTRY_COLLECTION_KEY_PREFIX)) return null;

	const remainder = collectionKey.slice(WORKFLOW_STATE_ENTRY_COLLECTION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, encodedStepKey, encodedResourceTypeHandle, direction, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || !encodedStepKey || !encodedResourceTypeHandle || !direction || extra !== undefined) return null;
	if (direction !== 'input' && direction !== 'output') return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
		stepKey: decodeURIComponent(encodedStepKey),
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
		direction,
	};
}

export function buildWorkflowStateEntryConstellationKey(args: {
	stepKey: string;
	resourceTypeHandle: string;
	direction: RoleDirection;
} & WorkflowKeyScope): ConstellationKey<'entity'> {
	return `${WORKFLOW_STATE_ENTRY_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}:${normalizeRoleKeyPart(args.stepKey)}:${normalizeRoleKeyPart(args.resourceTypeHandle)}:${args.direction}` as ConstellationKey<'entity'>;
}

export function parseWorkflowStateEntryConstellationKey(
	constellationKey: string
): {
	workflowHandle: string;
	threadIndex: number;
	stepKey: string;
	resourceTypeHandle: string;
	direction: RoleDirection;
} | null {
	if (!constellationKey.startsWith(WORKFLOW_STATE_ENTRY_CONSTELLATION_KEY_PREFIX)) return null;

	const remainder = constellationKey.slice(WORKFLOW_STATE_ENTRY_CONSTELLATION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, encodedStepKey, encodedResourceTypeHandle, direction, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || !encodedStepKey || !encodedResourceTypeHandle || !direction || extra !== undefined) return null;
	if (direction !== 'input' && direction !== 'output') return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
		stepKey: decodeURIComponent(encodedStepKey),
		resourceTypeHandle: decodeURIComponent(encodedResourceTypeHandle),
		direction,
	};
}

// ---------------------------------------------------------------------------
// Case key codec
// ---------------------------------------------------------------------------

export function buildCaseItemKey(args: {
	ownerMacroStepKey: string;
	ownerCaseIndex: number;
}): EntityItem['itemKey'] {
	const { ownerMacroStepKey, ownerCaseIndex } = args;
	return `item:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Case}:${normalizeRoleKeyPart(ownerMacroStepKey)}:${ownerCaseIndex}` as EntityItem['itemKey'];
}

export function parseCaseItemKey(itemKey: string): {
	ownerMacroStepKey: string;
	ownerCaseIndex: number;
} | null {
	const prefix = `item:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_Case}:`;
	if (!itemKey.startsWith(prefix)) return null;

	const remainder = itemKey.slice(prefix.length);
	const [encodedOwnerMacroStepKey, ownerCaseIndexRaw, extra] = remainder.split(':');
	if (!encodedOwnerMacroStepKey || !ownerCaseIndexRaw || extra !== undefined) return null;

	const ownerCaseIndex = Number.parseInt(ownerCaseIndexRaw, 10);
	if (!Number.isFinite(ownerCaseIndex) || ownerCaseIndex < 0) return null;

	return {
		ownerMacroStepKey: decodeURIComponent(encodedOwnerMacroStepKey),
		ownerCaseIndex,
	};
}

export function buildCaseCollectionKey(args: {
	ownerMacroStepKey: string;
} & WorkflowKeyScope): CollectionKey<'entity'> {
	return `${CASE_COLLECTION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}:${normalizeRoleKeyPart(args.ownerMacroStepKey)}` as CollectionKey<'entity'>;
}

export function parseCaseCollectionKey(collectionKey: string): {
	workflowHandle: string;
	threadIndex: number;
	ownerMacroStepKey: string;
} | null {
	if (!collectionKey.startsWith(CASE_COLLECTION_KEY_PREFIX)) return null;

	const remainder = collectionKey.slice(CASE_COLLECTION_KEY_PREFIX.length);
	const [encodedWorkflowHandle, threadIndexText, encodedOwnerMacroStepKey, extra] = remainder.split(':');
	if (!encodedWorkflowHandle || threadIndexText === undefined || !encodedOwnerMacroStepKey || extra !== undefined) return null;

	const threadIndex = Number(threadIndexText);
	if (!Number.isInteger(threadIndex) || threadIndex < 0) return null;

	return {
		workflowHandle: decodeURIComponent(encodedWorkflowHandle),
		threadIndex,
		ownerMacroStepKey: decodeURIComponent(encodedOwnerMacroStepKey),
	};
}

export function buildCaseConstellationKey(args: {
	ownerMacroStepKey: string;
} & WorkflowKeyScope): ConstellationKey<'entity'> {
	return `${CASE_CONSTELLATION_KEY_PREFIX}${normalizeRoleKeyPart(args.workflowHandle)}:${args.threadIndex}:${normalizeRoleKeyPart(args.ownerMacroStepKey)}` as ConstellationKey<'entity'>;
}
